import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight, CheckCircle2, FileUp, Loader2, RefreshCw, Search } from 'lucide-react';
import { api, apiDelete, apiGet, apiPost } from '@/lib/api';
import { fmtTime, fmtBytes } from '@/lib/format';
import { order } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { DataTable, type Column } from '@/components/DataTable';
import { Drawer } from '@/components/Drawer';
import type {
  OtaPackage,
  OtaPackageDetail,
  OtaPackageDeploymentSummary,
  PackageKind,
  CompatResult,
  Deployment,
  DeploymentState,
  DeploymentDetail,
  DeploymentTarget,
  TargetState,
  Device,
} from '@/types';

/* ── 狀態對照表，搬自原生版 ota.js ── */
const KIND_LABEL: Record<PackageKind, string> = { firmware: '韌體', model: '模型' };
const ESP32_HARDWARE = 'xiao-esp32s3-sense';
const STM32_HARDWARE = 'OpenMV N6';
const STM32_DEVICE_ID = 'n6-0015002e364350020030344d';
type PackageInfo = Pick<OtaPackage, 'name' | 'version' | 'hardware' | 'kind' | 'bytes'>;
type TargetWithResult = DeploymentTarget & {
  command_result?: { reason?: string; message?: string; downloaded_bytes?: number; total_bytes?: number } | null;
};
const FAILURE_REASON: Record<string, string> = {
  sha_mismatch: '檔案 SHA-256 不符，未切換韌體', download_failed: '下載失敗，請檢查網路與下載授權',
  http_status: '下載伺服器拒絕請求，請檢查授權與 API 連線', size_mismatch: '下載位元組數與套件不符',
  flash_failed: '寫入 Flash 失敗', invalid_image: '韌體映像驗證未通過',
  version_mismatch: 'BIN 內嵌版本或專案名稱與套件不符', journal_failed: '更新紀錄無法保存，請檢查裝置儲存',
  self_check_failed: '重開後網路自檢未通過', rollback_failed: '無法退回舊韌體，需要現場處理',
  rolled_back: '裝置已回到舊槽，更新未採用', interrupted: '下載期間重開或斷電，需重新建立部署',
  clock_unsynced: '裝置尚未對時，請確認 NTP 連線', deadline_expired: '超過更新期限',
  no_partition: '沒有足夠的 OTA 槽，請先核對 USB 安裝與分割表',
  busy: '裝置已有更新工作，請等待完成', busy_streaming: '即時觀看中，請先停止觀看',
  busy_uploading: '檔案上傳尚未讓出資源', busy_recording: '錄影尚未安全暫停',
  cancelled: '下載已取消，保留目前韌體', no_memory: '裝置記憶體不足',
};

const DEP_LABEL: Record<DeploymentState, string> = {
  pending: '等待下發', running: '更新進行中', done: '全部更新已確認', partial: '已結束・部分未完成',
  cancelled: '已取消', expired: '已過期',
};
const DEP_VARIANT: Record<DeploymentState, 'muted' | 'info' | 'success' | 'warn' | 'danger'> = {
  pending: 'muted', running: 'info', done: 'success', partial: 'warn',
  cancelled: 'muted', expired: 'danger',
};

const TGT_LABEL: Record<TargetState, string> = {
  pending: '等待下發', sent: '等待裝置受理', downloading: '下載並寫入', applying: '驗證與套用',
  rebooting: '等待重開確認', applied: '更新已確認', failed: '更新失敗', skipped: '等待即時觀看結束',
  cancelled: '已取消', expired: '已過期',
};
const TGT_VARIANT: Record<TargetState, 'muted' | 'info' | 'success' | 'warn' | 'danger'> = {
  pending: 'muted', sent: 'info', downloading: 'info', applying: 'info',
  rebooting: 'info', applied: 'success', failed: 'danger', skipped: 'warn',
  cancelled: 'muted', expired: 'danger',
};

/* ── 頁面元件 ── */
export default function OtaPage() {
  const queryClient = useQueryClient();

  /* 分頁切換 */
  const [tab, setTab] = useState<'packages' | 'deployments'>('packages');

  /* 抽屜 */
  const [pkgDetailId, setPkgDetailId] = useState<number | null>(null);
  const [depDetailId, setDepDetailId] = useState<number | null>(null);
  const [createDeployPkgId, setCreateDeployPkgId] = useState<number | null>(null);
  const [createDeployPkgInfo, setCreateDeployPkgInfo] = useState<PackageInfo | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  return (
    <>
      <Card className="reveal mb-5 overflow-hidden" style={order(0)}>
        <div className="border-l-4 border-primary px-5 py-5 sm:px-6">
          <p className="text-[11px] font-bold tracking-[0.16em] text-primary">OTA / 遠端更新</p>
          <h2 className="mt-2 font-display text-xl font-bold">先準備檔案，再確認裝置</h2>
          <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">上傳不會立即更新裝置。確認版本、硬體與選取名單後，才開始遠端下載與重開。</p>
          <ol className="mt-5 grid gap-3 text-[13px] sm:grid-cols-3">
            {['上傳韌體或模型', '選擇相容裝置', '確認部署並追蹤結果'].map((label, index) => (
              <li key={label} className="flex min-w-0 items-center gap-2">
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-secondary font-mono font-bold">{index + 1}</span>
                <span>{label}</span>
              </li>
            ))}
          </ol>
        </div>
      </Card>
      {/* 分頁切換按鈕 */}
      <div className="reveal mb-4 flex gap-2" style={order(1)} aria-label="OTA 工作區">
        <Button
          size="sm"
          variant={tab === 'packages' ? 'default' : 'secondary'}
          onClick={() => setTab('packages')}
          aria-pressed={tab === 'packages'}
        >
          套件與上傳
        </Button>
        <Button
          size="sm"
          variant={tab === 'deployments' ? 'default' : 'secondary'}
          onClick={() => setTab('deployments')}
          aria-pressed={tab === 'deployments'}
        >
          部署進度
        </Button>
      </div>

      {/* key=tab：切換分頁時內容重播一次進場 */}
      <div key={tab} className="reveal" style={order(2)}>
      {tab === 'packages' ? (
        <PackagesTab
          onViewDetail={(id) => { setPkgDetailId(id); }}
          onUpload={() => setUploadOpen(true)}
          onChoose={(pkg) => { setCreateDeployPkgId(pkg.id); setCreateDeployPkgInfo(pkg); }}
        />
      ) : (
        <DeploymentsTab
          onViewDetail={(id) => setDepDetailId(id)}
          onChoosePackage={() => setTab('packages')}
        />
      )}
      </div>

      {/* 套件詳情抽屜 */}
      <UploadPackageDrawer open={uploadOpen} onClose={() => setUploadOpen(false)} onChoose={(id) => {
        setUploadOpen(false); setCreateDeployPkgId(id); setCreateDeployPkgInfo(null);
      }} />
      <PkgDetailDrawer
        key={`pkg-${pkgDetailId ?? 'closed'}`}
        pkgId={pkgDetailId}
        onClose={() => setPkgDetailId(null)}
        onCreateDeploy={(id, info) => {
          setPkgDetailId(null);
          setCreateDeployPkgId(id);
          setCreateDeployPkgInfo(info);
        }}
      />

      {/* 建立部署抽屜 */}
      <CreateDeployDrawer
        key={`deploy-${createDeployPkgId ?? 'closed'}`}
        pkgId={createDeployPkgId}
        pkgInfo={createDeployPkgInfo}
        onClose={() => { setCreateDeployPkgId(null); setCreateDeployPkgInfo(null); }}
        onCreated={(id) => {
          setCreateDeployPkgId(null);
          setCreateDeployPkgInfo(null);
          queryClient.invalidateQueries({ queryKey: ['deployments'] });
          setTab('deployments');
          setDepDetailId(id);
        }}
      />

      {/* 部署詳情抽屜 */}
      <DepDetailDrawer
        depId={depDetailId}
        onClose={() => setDepDetailId(null)}
      />
    </>
  );
}

/* =============================== 套件分頁 =============================== */

function PackagesTab({ onViewDetail, onUpload, onChoose }: {
  onViewDetail: (id: number) => void; onUpload: () => void; onChoose: (pkg: OtaPackage) => void;
}) {
  const [search, setSearch] = useState('');
  const pkgsQ = useQuery({
    queryKey: ['packages'],
    queryFn: () => apiGet<OtaPackage[]>('/api/v1/packages'),
  });

  const columns: Column<OtaPackage>[] = [
    {
      key: 'kind', header: '種類',
      cell: (p) => (
        <Badge variant={p.kind === 'firmware' ? 'info' : 'success'}>
          {KIND_LABEL[p.kind] ?? p.kind}
        </Badge>
      ),
    },
    { key: 'name', header: '名稱', cell: (p) => p.name },
    { key: 'version', header: '版本', cell: (p) => p.version },
    { key: 'hardware', header: '硬體', cell: (p) => p.hardware },
    {
      key: 'bytes', header: '大小', align: 'right', className: 'tabular',
      cell: (p) => fmtBytes(p.bytes),
    },
    {
      key: 'sha256', header: 'SHA-256',
      cell: (p) => <span className="font-mono text-[11.5px]">{(p.sha256 ?? '').substring(0, 12)}</span>,
    },
    { key: 'uploaded_by', header: '上傳者', cell: (p) => p.uploaded_by_name ?? '—' },
    {
      key: 'deploy_count', header: '部署次數', align: 'right', className: 'tabular',
      cell: (p) => String(p.deploy_count ?? 0),
    },
    {
      key: 'actions', header: '操作',
      cell: (p) => (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => onChoose(p)}>選擇裝置</Button>
          <Button size="sm" variant="secondary" onClick={() => onViewDetail(p.id)}>詳情</Button>
        </div>
      ),
    },
  ];

  return (
    <Card className="admin-data-card overflow-hidden">
      <CardHeader>
        <div className="flex w-full flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>套件管理</CardTitle>
            <CardDescription>選擇已有套件，或先上傳要更新的檔案。</CardDescription>
          </div>
          <Button onClick={onUpload}><FileUp className="h-4 w-4" />上傳套件</Button>
        </div>
      </CardHeader>
      {Boolean(pkgsQ.data?.length) && <div className="mx-5 mb-3 mt-4 sm:mx-6">
        <Input aria-label="搜尋套件名稱、版本或硬體" placeholder="搜尋套件名稱、版本或硬體" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>}
      {!pkgsQ.isLoading && !pkgsQ.error && pkgsQ.data?.length === 0 && (
        <div className="mx-4 mb-4 border-l-2 border-primary bg-secondary/40 px-4 py-3 text-[13px] sm:mx-6">
          <p className="font-semibold">開始部署前，先上傳套件</p>
          <p className="mt-1 text-muted-foreground">上傳韌體或模型 → 檢查裝置相容性 → 建立部署。</p>
        </div>
      )}
      <DataTable<OtaPackage>
        rows={(pkgsQ.data ?? []).filter((p) => `${p.name} ${p.version} ${p.hardware}`.toLowerCase().includes(search.trim().toLowerCase()))}
        columns={columns}
        rowKey={(p) => String(p.id)}
        loading={pkgsQ.isLoading}
        error={pkgsQ.error}
        empty={search ? '沒有符合搜尋條件的套件，請調整關鍵字。' : '尚無套件'}
      />
    </Card>
  );
}

function UploadPackageDrawer({ open, onClose, onChoose }: {
  open: boolean; onClose: () => void; onChoose: (id: number) => void;
}) {
  const queryClient = useQueryClient();
  const [formKey, setFormKey] = useState(0);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [kind, setKind] = useState<PackageKind>('firmware');
  const [hardware, setHardware] = useState('');
  const [fileInfo, setFileInfo] = useState<{ name: string; size: number } | null>(null);
  const [uploadedId, setUploadedId] = useState<number | null>(null);
  const stm32Selected = kind === 'firmware' && hardware.trim() === STM32_HARDWARE;
  const devicesQ = useQuery({
    queryKey: ['devices'],
    queryFn: () => apiGet<Device[]>('/api/v1/devices'),
    enabled: open && stm32Selected,
  });
  const stm32Device = devicesQ.data?.find((device) => device.device_id === STM32_DEVICE_ID);
  const uploadMut = useMutation({
    mutationFn: (body: FormData) => api<{ id: number; name: string; version: string }>('/api/v1/packages', { method: 'POST', body }),
    onSuccess: (pkg) => {
      setFormKey((key) => key + 1);
      setError('');
      setSuccess(`已上傳 ${pkg.name} · ${pkg.version}。檔案已保存，尚未下發更新。`);
      setUploadedId(pkg.id);
      setFileInfo(null);
      queryClient.invalidateQueries({ queryKey: ['packages'] });
    },
    onError: (err) => setError(err instanceof Error ? err.message : '上傳失敗，請重試'),
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const body = new FormData(event.currentTarget);
    const file = body.get('file');
    if (!(file instanceof File) || !file.name) { setError('請選擇套件檔案'); return; }
    if (!file.size) { setError('檔案是空的，請選擇完整套件'); return; }
    if (file.size > 64 * 1024 * 1024) { setError('套件大小不可超過 64 MB'); return; }
    if (!/\.(bin|fw|tflite|onnx|zip)$/i.test(file.name)) { setError('僅接受 .bin、.fw、.tflite、.onnx 或 .zip 檔案'); return; }
    if (stm32Selected && !/\.bin$/i.test(file.name)) { setError('STM32／OpenMV N6 韌體請使用應用程式 .bin'); return; }
    if (kind === 'firmware' && hardware.trim() === ESP32_HARDWARE) {
      if (!/\.bin$/i.test(file.name)) { setError('XIAO ESP32-S3 OTA 請使用應用程式 .bin'); return; }
      if (file.size > 3 * 1024 * 1024) { setError('XIAO ESP32-S3 的 OTA 槽上限為 3 MiB，請檢查韌體大小'); return; }
      if (String(body.get('version')).length > 31) { setError('ESP32 韌體版本不可超過 31 個字元'); return; }
    }
    for (const field of ['name', 'version', 'hardware']) {
      const value = String(body.get(field) ?? '').trim();
      if (!value) { setError('請完整填寫套件名稱、版本與硬體代號'); return; }
      body.set(field, value);
    }
    setError('');
    setSuccess('');
    setUploadedId(null);
    uploadMut.mutate(body);
  };

  return (
    <Drawer open={open} onOpenChange={(value) => { if (!value && !uploadMut.isPending) onClose(); }} title="步驟 1 · 上傳套件">
      <form key={formKey} onSubmit={handleSubmit} className="space-y-4">
        <p className="text-[13px] leading-relaxed text-muted-foreground">這一步只保存檔案。下一步選裝置、檢查相容性，最後確認才開始更新。</p>
        <label className="block text-[13px] font-semibold">套件種類
          <select name="kind" value={kind} onChange={(e) => setKind(e.target.value as PackageKind)} required disabled={uploadMut.isPending} className="mt-1 min-h-11 w-full rounded-sm border border-border bg-card px-3 text-foreground">
            <option value="firmware">韌體</option><option value="model">模型</option>
          </select>
        </label>
        {([['name', '套件名稱'], ['version', '韌體／模型版本']] as const).map(([name, label]) => (
          <label key={name} className="block text-[13px] font-semibold">{label}
            <Input name={name} required disabled={uploadMut.isPending} className="mt-1" placeholder={name === 'version' ? '與檔案內嵌版本完全一致' : '例如：相機韌體'} />
          </label>
        ))}
        <label className="block text-[13px] font-semibold">適用硬體代號
          <Input name="hardware" required value={hardware} onChange={(e) => { setHardware(e.target.value); setError(''); }} disabled={uploadMut.isPending} className="mt-1 font-mono" placeholder="須與裝置上報的硬體代號完全一致" />
        </label>
        <div className="rounded-sm border border-border bg-secondary/35 p-3 text-[12px] leading-relaxed">
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant={kind === 'firmware' && hardware.trim() === ESP32_HARDWARE ? 'default' : 'secondary'} aria-pressed={kind === 'firmware' && hardware.trim() === ESP32_HARDWARE} className="max-w-full whitespace-normal text-left" disabled={uploadMut.isPending} onClick={() => { setHardware(ESP32_HARDWARE); setKind('firmware'); setError(''); }}>使用 XIAO ESP32-S3 硬體代號</Button>
            <Button type="button" size="sm" variant={stm32Selected ? 'default' : 'secondary'} aria-pressed={stm32Selected} className="max-w-full whitespace-normal text-left" disabled={uploadMut.isPending} onClick={() => { setHardware(STM32_HARDWARE); setKind('firmware'); setError(''); }}>使用 STM32／OpenMV N6 硬體代號</Button>
          </div>
          <p className="mt-2 text-muted-foreground">快捷選項也可於上方自行修改硬體代號。</p>
          {kind === 'firmware' && hardware.trim() === ESP32_HARDWARE && <>
            <p className="mt-2 break-all font-mono">{ESP32_HARDWARE}</p>
            <p className="mt-1 text-muted-foreground">ESP32 請上傳應用程式 .bin（最多 3 MiB），不要選 bootloader、分割表或合併燒錄檔。版本須和 BIN 內嵌版本一致；首次安裝 OTA 功能仍需 USB 燒錄。</p>
          </>}
          {stm32Selected && <>
            <p className="mt-2 font-mono">{STM32_HARDWARE}</p>
            <p className="mt-1 text-muted-foreground">STM32 使用應用程式 .bin；勿選 bootloader、分割表或合併檔。版本須和 BIN 內嵌版本完全一致，硬體代號須符合裝置上報值。容量以板端設計為準。</p>
            <p className="mt-2 break-all font-mono">{STM32_DEVICE_ID}</p>
            <p className="mt-1 text-muted-foreground">{!devicesQ.isError && stm32Device?.capabilities?.firmware_ota === false
              ? '此裝置尚未宣告 firmware_ota；可保存檔案，目前不能下發 OTA。板端須先完成更新指令與安全開機契約。'
              : '上傳只保存檔案；需完成更新指令與安全開機契約、宣告 firmware_ota，再通過相容性檢查。'}</p>
          </>}
        </div>
        <label className="block text-[13px] font-semibold">套件檔案
          <input name="file" type="file" required accept={kind === 'firmware' && [ESP32_HARDWARE, STM32_HARDWARE].includes(hardware.trim()) ? '.bin' : '.bin,.fw,.tflite,.onnx,.zip'} disabled={uploadMut.isPending} onChange={(e) => {
            const file = e.target.files?.[0]; setFileInfo(file ? { name: file.name, size: file.size } : null); setError('');
          }} className="mt-1 block w-full min-w-0 rounded-sm border border-border bg-card p-2 text-[13px] file:mr-3 file:rounded-sm file:border-0 file:bg-secondary file:px-3 file:py-2 file:font-semibold" />
          {fileInfo && <span className="mt-2 block break-all text-[12px] font-normal text-muted-foreground">{fileInfo.name} · {fmtBytes(fileInfo.size)}</span>}
          <span className="mt-1 block text-[12px] font-normal text-muted-foreground">平台單次上傳上限為 64 MiB，不代表裝置可燒錄容量。</span>
        </label>
        <details className="text-[13px]">
          <summary className="cursor-pointer font-semibold">其他資訊（選填）</summary>
          <div className="mt-3 space-y-3">
            <label className="block">最低韌體版本<Input name="min_firmware" disabled={uploadMut.isPending} className="mt-1" /></label>
            <label className="block">簽章<Input name="signature" disabled={uploadMut.isPending} className="mt-1" /></label>
            <label className="block">備註<Input name="notes" disabled={uploadMut.isPending} className="mt-1" /></label>
            <label className="block">輸入規格（JSON）<textarea name="input_spec" disabled={uploadMut.isPending} className="mt-1 min-h-20 w-full rounded-sm border border-border bg-card p-3 font-mono text-[12px]" /></label>
          </div>
        </details>
        {error && <p role="alert" className="text-[13px] text-destructive">上傳失敗：{error}</p>}
        {success && <div role="status" className="rounded-sm border border-border bg-secondary/35 p-4">
          <p className="text-[13px] text-success-on">{success}</p>
          {uploadedId !== null && <Button type="button" className="mt-3" onClick={() => onChoose(uploadedId)}>下一步：選擇裝置<ArrowRight className="h-4 w-4" /></Button>}
        </div>}
        {uploadMut.isPending && <p role="status" className="flex items-center gap-2 text-[13px]"><Loader2 className="h-4 w-4 animate-spin" />正在保存檔案並計算 SHA-256…</p>}
        <Button type="submit" variant={uploadedId === null ? 'default' : 'secondary'} disabled={uploadMut.isPending}>{uploadMut.isPending ? '上傳中…' : uploadedId === null ? '上傳套件' : '上傳另一個套件'}</Button>
      </form>
    </Drawer>
  );
}

/* =============================== 套件詳情抽屜 =============================== */

function PkgDetailDrawer({
  pkgId,
  onClose,
  onCreateDeploy,
}: {
  pkgId: number | null;
  onClose: () => void;
  onCreateDeploy: (id: number, info: PackageInfo) => void;
}) {
  const queryClient = useQueryClient();
  const [withDeployments, setWithDeployments] = useState(false);
  // 換一個套件就重來，勾選不能沿用到下一個套件
  useEffect(() => { setWithDeployments(false); }, [pkgId]);
  const pkgQ = useQuery({
    queryKey: ['packages', pkgId],
    queryFn: () => apiGet<OtaPackageDetail>(`/api/v1/packages/${pkgId}`),
    enabled: pkgId !== null,
    staleTime: 0,
  });
  const deleteMut = useMutation({
    mutationFn: ({ id, withDeps }: { id: number; withDeps: boolean }) =>
      apiDelete(`/api/v1/packages/${id}${withDeps ? '?with_deployments=true' : ''}`),
    onSuccess: () => {
      onClose();
      queryClient.removeQueries({ queryKey: ['packages', pkgId], exact: true });
      queryClient.invalidateQueries({ queryKey: ['packages'], exact: true });
      queryClient.invalidateQueries({ queryKey: ['deployments'] });
    },
  });

  const data = pkgQ.data;
  const deploymentCount = data?.deployments?.length ?? 0;
  const activeDeployments = (data?.deployments ?? []).filter((d) => d.state === 'pending' || d.state === 'running').length;
  const deleteBlocked = activeDeployments > 0 || (deploymentCount > 0 && !withDeployments);
  const deleteHint = pkgQ.isFetching ? '正在確認部署引用，完成後才能刪除。'
    : activeDeployments > 0 ? `有 ${activeDeployments} 個部署仍在進行，請先在部署詳情取消或等它結束，才能刪除套件。`
    : deploymentCount > 0 && !withDeployments ? `此套件已被 ${deploymentCount} 個部署引用。勾選上方選項，可連同部署紀錄一起刪除。`
    : deploymentCount > 0 ? `會一併刪除 ${deploymentCount} 筆部署紀錄與逐台進度。不影響裝置目前的韌體，刪除後無法還原。`
    : '僅刪除平台套件與上傳檔，不影響裝置目前的韌體。刪除檔案無法還原。';

  return (
    <Drawer open={pkgId !== null} onOpenChange={(v) => { if (!v && !deleteMut.isPending) onClose(); }} title="套件詳情">
      {pkgQ.isLoading ? (
        <div className="grid place-items-center py-20">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : pkgQ.error ? (
        <p className="py-6 text-center text-[13px] text-destructive">
          載入失敗：{pkgQ.error instanceof Error ? pkgQ.error.message : '未知錯誤'}
        </p>
      ) : data ? (
        <div className="space-y-4">
          <h3 className="text-base font-bold">
            {KIND_LABEL[data.kind] ?? data.kind}：{data.name} · {data.version}
          </h3>
          <p className="text-[13px]">硬體：{data.hardware}　大小：{fmtBytes(data.bytes)}</p>
          <p className="break-all font-mono text-[12px]">SHA-256：{data.sha256}</p>
          {data.notes && <p className="text-[13px] text-muted-foreground">{data.notes}</p>}

          {deploymentCount > 0 && (
            <label className={`flex min-h-11 items-center gap-2 text-[13px] ${activeDeployments > 0 ? 'text-muted-foreground' : ''}`}>
              <input type="checkbox" checked={withDeployments && activeDeployments === 0}
                     disabled={activeDeployments > 0 || deleteMut.isPending}
                     onChange={(e) => setWithDeployments(e.target.checked)}
                     className="h-4 w-4 accent-primary" />
              連同 {deploymentCount} 筆部署紀錄一起刪除
            </label>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              disabled={deleteMut.isPending}
              onClick={() => onCreateDeploy(data.id, data)}
            >
              下一步：選擇裝置<ArrowRight className="h-4 w-4" />
            </Button>
            <Button
              variant="destructive"
              disabled={deleteBlocked || deleteMut.isPending || pkgQ.isFetching}
              aria-describedby="ota-package-delete-reason"
              onClick={() => {
                if (deleteBlocked || deleteMut.isPending || pkgQ.isFetching) return;
                const extra = deploymentCount > 0 ? `，以及 ${deploymentCount} 筆部署紀錄與逐台進度` : '';
                if (!window.confirm(`刪除${KIND_LABEL[data.kind]}套件「${data.name}」· ${data.version}？\n將永久刪除平台套件與上傳檔${extra}，無法還原；不會影響裝置目前的韌體。`)) return;
                deleteMut.mutate({ id: data.id, withDeps: deploymentCount > 0 });
              }}
            >
              {deleteMut.isPending ? '刪除中…' : `刪除${KIND_LABEL[data.kind]}套件`}
            </Button>
          </div>
          <p id="ota-package-delete-reason" className="text-[12px] leading-relaxed text-muted-foreground">{deleteHint}</p>
          {deleteMut.isError && <p role="alert" className="break-words text-[13px] text-destructive">刪除失敗：{deleteMut.error instanceof Error ? deleteMut.error.message : '請重試'}</p>}

          {data.deployments && data.deployments.length > 0 && (
            <div>
              <h4 className="mt-4 text-sm font-bold">部署紀錄</h4>
              <div className="mt-2 space-y-2">
                {data.deployments.map((d: OtaPackageDeploymentSummary) => (
                  <Card key={d.id} className="p-3">
                    <div className="flex items-center gap-2 text-[13px]">
                      <Badge variant={DEP_VARIANT[d.state as DeploymentState] ?? 'muted'}>
                        {DEP_LABEL[d.state as DeploymentState] ?? d.state}
                      </Badge>
                      <span>{d.name ?? '—'} — {d.applied_count}/{d.device_count} 台已確認</span>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : null}
    </Drawer>
  );
}

/* =============================== 建立部署抽屜 =============================== */

function CreateDeployDrawer({
  pkgId,
  pkgInfo,
  onClose,
  onCreated,
}: {
  pkgId: number | null;
  pkgInfo: PackageInfo | null;
  onClose: () => void;
  onCreated: (id: number) => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deployName, setDeployName] = useState('');
  const [ttlMin, setTtlMin] = useState('60');
  const [warnMsg, setWarnMsg] = useState('');
  const [search, setSearch] = useState('');
  const [onlyCompatible, setOnlyCompatible] = useState(false);
  const [review, setReview] = useState(false);
  const reviewHeading = useRef<HTMLHeadingElement>(null);
  useEffect(() => { if (review) reviewHeading.current?.focus(); }, [review]);
  const pkgQ = useQuery({
    queryKey: ['packages', pkgId],
    queryFn: () => apiGet<OtaPackageDetail>(`/api/v1/packages/${pkgId}`),
    enabled: pkgId !== null,
  });
  const pkg = pkgQ.data ?? pkgInfo;

  /* 取得裝置清單 */
  const devicesQ = useQuery({
    queryKey: ['devices'],
    queryFn: () => apiGet<Device[]>('/api/v1/devices'),
    enabled: pkgId !== null,
    refetchInterval: pkgId !== null ? 10_000 : false,
  });

  /* 相容性檢查 */
  const deviceIds = (devicesQ.data ?? []).map((d) => d.device_id).join(',');
  const compatQ = useQuery({
    queryKey: ['packages', pkgId, 'compat', deviceIds],
    queryFn: () => apiGet<CompatResult[]>(`/api/v1/packages/${pkgId}/compat?device_ids=${encodeURIComponent(deviceIds)}`),
    enabled: pkgId !== null && deviceIds.length > 0,
    refetchInterval: pkgId !== null && deviceIds.length > 0 ? 10_000 : false,
  });

  const compatMap = new Map<string, CompatResult>();
  for (const c of compatQ.data ?? []) {
    compatMap.set(c.device_id, c);
  }
  const devices = devicesQ.data ?? [];
  // 與後端一致：批次比例的分母只算已認領的裝置
  const claimedCount = devices.filter((d) => (d.claim_state ?? 'claimed') === 'claimed').length;
  const batchLimit = Math.min(20, Math.floor(claimedCount * 0.5));
  const reasonsFor = (device: Device): string[] => [
    ...(compatMap.get(device.device_id)?.reasons ?? []),
    ...(!device.online && !compatMap.get(device.device_id)?.reasons.some((reason) => reason.includes('離線')) ? ['裝置離線，請恢復網路後重新檢查'] : []),
    ...((device.claim_state === 'pending' || device.claim_state === 'rejected')
      && !compatMap.get(device.device_id)?.reasons.some((reason) => reason.includes('認領')) ? ['請先至裝置頁完成認領'] : []),
  ];
  const canChoose = (device: Device) => compatMap.get(device.device_id)?.ok === true && reasonsFor(device).length === 0;
  const selectedDevices = devices.filter((device) => selected.has(device.device_id));
  const allSelectedCompatible = selectedDevices.length === selected.size && selectedDevices.every(canChoose);
  const compatibleCount = devices.filter(canChoose).length;
  const duration = Number(ttlMin);
  const validDuration = ttlMin.trim() !== '' && Number.isInteger(duration) && duration >= 1 && duration <= 1440;
  const overLimit = selected.size > batchLimit;
  const ready = Boolean(pkg) && selected.size > 0 && !overLimit && validDuration
    && allSelectedCompatible && !compatQ.isFetching && !compatQ.isError && !devicesQ.isError;
  const visibleDevices = devices.filter((device) =>
    (!onlyCompatible || canChoose(device) || selected.has(device.device_id)) && `${device.device_id} ${device.label ?? ''}`.toLowerCase().includes(search.trim().toLowerCase()));

  /* 送出部署 */
  const createMut = useMutation({
    mutationFn: (body: { package_id: number; device_ids: string[]; name?: string; ttl_min: number }) =>
      apiPost<{ id: number }>('/api/v1/deployments', body),
    onSuccess: (result) => {
      onCreated(result.id);
    },
  });

  const handleToggle = (deviceId: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(deviceId); else next.delete(deviceId);
      return next;
    });
  };

  const handleSubmit = () => {
    const ids = [...selected];
    if (ids.length === 0) { setWarnMsg('請至少選一台裝置'); return; }
    if (pkgId === null || !review || createMut.isPending) return;
    if (!ready) { setWarnMsg('裝置狀態或部署條件已變更，請返回選擇並重新檢查。'); return; }
    setWarnMsg('');
    createMut.mutate({
      package_id: pkgId,
      device_ids: ids,
      name: deployName || undefined,
      ttl_min: duration,
    });
  };
  const handleReview = async () => {
    if (!ready) return;
    setWarnMsg('');
    const checked = await compatQ.refetch();
    if (checked.error || !checked.data) { setWarnMsg('相容性檢查未完成，請重試。'); return; }
    if ([...selected].some((id) => !checked.data.some((item) => item.device_id === id && item.ok))) {
      setWarnMsg('選取裝置的相容性已變更，請查看原因並重新選擇。'); return;
    }
    setReview(true);
  };

  return (
    <Drawer
      open={pkgId !== null}
      onOpenChange={(v) => { if (!v && !createMut.isPending) onClose(); }}
      title={review ? '步驟 3 · 確認部署' : '步驟 2 · 選擇裝置'}
    >
      {devicesQ.isLoading || (!pkg && pkgQ.isLoading) ? (
        <div className="grid place-items-center py-20">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : devicesQ.error || (!pkg && pkgQ.error) ? (
        <div className="space-y-3 py-6 text-[13px]">
          <p role="alert" className="text-destructive">套件或裝置清單載入失敗，請重試。</p>
          <Button variant="secondary" onClick={() => { void devicesQ.refetch(); void pkgQ.refetch(); }}>重新載入</Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-sm border border-border bg-secondary/35 p-4">
            <p className="text-[11px] font-semibold tracking-wide text-muted-foreground">本次更新套件</p>
            <p className="mt-1 break-words font-semibold">{pkg?.name} · {pkg?.version}</p>
            <p className="mt-2 break-all font-mono text-[12px]">{pkg?.hardware} · {fmtBytes(pkg?.bytes ?? 0)}</p>
            {pkg?.hardware === ESP32_HARDWARE && <p className="mt-2 text-[12px] leading-relaxed text-muted-foreground">僅適用已安裝 OTA 功能的 XIAO ESP32-S3。未宣告 OTA 能力的舊韌體需先透過 USB 安裝。</p>}
          </div>
          {review ? <>
            <h3 ref={reviewHeading} tabIndex={-1} className="text-base font-bold outline-none">確認這 {selected.size} 台裝置要更新</h3>
            <div className="space-y-2">
              {selectedDevices.map((device) => <div key={device.device_id} className="rounded-sm border border-border px-3 py-3 text-[13px]">
                <p className="break-words font-semibold">{device.label || device.device_id}</p>
                <p className="mt-1 break-all font-mono text-[12px] text-muted-foreground">{device.device_id}</p>
                <p className="mt-2 break-all">{(pkg?.kind === 'model' ? device.model_version : device.firmware_version) || '版本未回報'} → <span className="font-semibold">{pkg?.version}</span></p>
              </div>)}
            </div>
            <p className="text-[13px]">完成期限：{duration} 分鐘。{deployName && `部署名稱：${deployName}`}</p>
            <div className="flex gap-3 border-l-2 border-primary bg-secondary/35 px-4 py-3 text-[13px] leading-relaxed">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <p>開始後裝置會下載、驗證並重開，錄影與上傳可能暫停。請保持電源和網路；下載 100% 後仍要等待重開確認。</p>
            </div>
            {!allSelectedCompatible && <p role="alert" className="text-[13px] text-destructive">部分裝置狀態已變更，請返回選擇並重新檢查。</p>}
          </> : <>
          <div className="flex items-center gap-2 text-[13px] font-semibold"><Search className="h-4 w-4 text-muted-foreground" />找出要更新的裝置</div>
          <Input aria-label="搜尋裝置代號或名稱" placeholder="搜尋裝置代號或名稱" value={search} onChange={(e) => setSearch(e.target.value)} />
          <label className="flex min-h-11 items-center gap-2 text-[13px]">
            <input type="checkbox" checked={onlyCompatible} onChange={(e) => setOnlyCompatible(e.target.checked)} className="h-4 w-4 accent-primary" />只顯示可更新裝置
          </label>
          {onlyCompatible && selected.size > 0 && <p className="text-[12px] text-muted-foreground">已選裝置會保留在清單中，方便取消勾選。</p>}
          <div className="flex flex-wrap items-center justify-between gap-2 text-[13px]" aria-live="polite">
            <p className={overLimit ? 'font-semibold text-destructive' : 'font-semibold'}>已選 {selected.size} 台 / 上限 {batchLimit} 台</p>
            <span className="text-muted-foreground">{compatQ.isLoading ? '檢查中' : `${compatibleCount} 台可更新 · 共 ${devices.length} 台`}</span>
          </div>
          <p className="text-[12px] leading-relaxed text-muted-foreground">每批最多全平台裝置的 50%，且不超過 20 台。{batchLimit === 0 ? '目前裝置數不足以通過批次政策，請由平台管理員確認。' : '建議先選一台確認成功，再逐批更新。'}</p>
          {overLimit && <p role="alert" className="text-[13px] text-destructive">超過本批上限，請取消部分勾選後繼續。</p>}
          {selected.size > 0 && !allSelectedCompatible && <p role="alert" className="text-[13px] text-destructive">部分已選裝置已離線或不相容，請清除搜尋並查看原因、取消勾選後繼續。</p>}
          {compatQ.isLoading && <p role="status" className="flex items-center gap-2 text-[13px]"><Loader2 className="h-4 w-4 animate-spin" />正在檢查硬體、版本與連線狀態…</p>}
          {compatQ.isError && <div className="space-y-2 text-[13px]">
            <p role="alert" className="text-destructive">相容性檢查失敗，尚不能選擇裝置或送出部署。</p>
            <Button size="sm" variant="secondary" onClick={() => void compatQ.refetch()}><RefreshCw className="h-4 w-4" />重新檢查</Button>
          </div>}
          {/* 裝置勾選清單 */}
          <div className="max-h-[360px] space-y-2 overflow-y-auto">
            {visibleDevices.map((d) => {
              const compatible = canChoose(d) && !compatQ.isError;
              const reasons = reasonsFor(d);
              const checking = !compatMap.has(d.device_id) && !compatQ.isError;
              return (
                <label key={d.device_id} className={`flex min-w-0 items-start gap-3 rounded-sm border p-3 text-[13px] ${selected.has(d.device_id) ? 'border-primary bg-accent/35' : 'border-border bg-card'}`}>
                  <input
                    type="checkbox"
                    disabled={(!compatible || checking || batchLimit === 0) && !selected.has(d.device_id)}
                    checked={selected.has(d.device_id)}
                    onChange={(e) => handleToggle(d.device_id, e.target.checked)}
                    className="mt-1 h-[18px] w-[18px] shrink-0 accent-primary"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block break-words font-semibold">{d.label || d.device_id}</span>
                    <span className="mt-1 block break-all font-mono text-[12px] text-muted-foreground">{d.device_id}</span>
                    <span className="mt-2 block break-all text-[12px]">目前版本：{(pkg?.kind === 'model' ? d.model_version : d.firmware_version) || '未回報'}</span>
                    <span className="mt-2 block"><Badge variant={checking || compatQ.isError ? 'muted' : compatible ? 'success' : 'warn'}>{checking ? '檢查中' : compatQ.isError ? '尚未完成檢查' : compatible ? '可更新' : '目前無法更新'}</Badge></span>
                    {!compatible && !checking && !compatQ.isError && <span className="mt-2 block break-words text-[12px] leading-relaxed text-muted-foreground">{reasons.join('；') || '尚未取得相容性結果'}{reasons.some((reason) => reason.includes('firmware_ota')) ? '。舊版 ESP32 請先透過 USB 安裝 OTA 韌體。' : ''}</span>}
                  </span>
                </label>
              );
            })}
          </div>
          {!visibleDevices.length && <p className="py-4 text-[13px] text-muted-foreground">{devices.length ? '沒有符合條件的裝置，請清除搜尋或關閉篩選。' : '尚無裝置。請先完成裝置上線與認領。'}</p>}

          <div>
            <label htmlFor="ota-deploy-name" className="mb-1 block text-[13px] font-semibold">部署名稱（選填）</label>
            <Input
              id="ota-deploy-name"
              placeholder="部署名稱（選填）"
              value={deployName}
              onChange={(e) => setDeployName(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="ota-deploy-ttl" className="mb-1 block text-[13px] font-semibold">完成期限（分鐘）</label>
            <Input
              id="ota-deploy-ttl"
              type="number"
              min={1}
              max={1440}
              step={1}
              value={ttlMin}
              onChange={(e) => setTtlMin(e.target.value)}
            />
            <p className="mt-1 text-[12px] text-muted-foreground">1–1440 分鐘。到期仍未確認的裝置會標記逾期。</p>
            {!validDuration && <p role="alert" className="mt-1 text-[12px] text-destructive">請輸入 1–1440 的整數分鐘。</p>}
          </div>
          </>}

          {warnMsg && <p role="alert" className="text-[13px] text-destructive">{warnMsg}</p>}
          {createMut.isError && (
            <p role="alert" className="break-words text-[13px] text-destructive">
              送出失敗：{createMut.error instanceof Error ? createMut.error.message : '未知錯誤'}
            </p>
          )}

          <div className="flex flex-wrap gap-2 border-t border-border pt-4">
            {review && <Button variant="secondary" disabled={createMut.isPending} onClick={() => { setReview(false); setWarnMsg(''); createMut.reset(); }}>返回選擇</Button>}
            <Button disabled={!ready || createMut.isPending} onClick={review ? handleSubmit : handleReview}>
              {createMut.isPending ? '正在建立部署…' : review ? '確認並開始更新' : compatQ.isFetching ? '正在檢查…' : '下一步：確認部署'}
              {!createMut.isPending && <ArrowRight className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      )}
    </Drawer>
  );
}

/* =============================== 部署分頁 =============================== */

function DeploymentsTab({ onViewDetail, onChoosePackage }: { onViewDetail: (id: number) => void; onChoosePackage: () => void }) {
  const depsQ = useQuery({
    queryKey: ['deployments'],
    queryFn: () => apiGet<Deployment[]>('/api/v1/deployments'),
    refetchInterval: 5_000, // 部署進度定期更新
  });

  const columns: Column<Deployment>[] = [
    { key: 'name', header: '名稱', cell: (d) => d.name ?? '—' },
    {
      key: 'package', header: '套件',
      cell: (d) => `${KIND_LABEL[d.kind as PackageKind] ?? d.kind} ${d.pkg_name} · ${d.pkg_version}`,
    },
    {
      key: 'state', header: '狀態',
      cell: (d) => (
        <Badge variant={DEP_VARIANT[d.state]}>
          {DEP_LABEL[d.state]}
        </Badge>
      ),
    },
    {
      key: 'progress', header: '已確認裝置',
      cell: (d) => {
        const pct = d.device_count ? Math.round(d.applied_count / d.device_count * 100) : 0;
        return (
          <div className="flex items-center gap-2">
            <div className="h-2 w-20 rounded-full bg-secondary" role="progressbar" aria-label="更新已確認比例" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
              <div
                className={`h-full rounded-full transition-all ${d.state === 'done' ? 'bg-success' : 'bg-primary'} ${d.state === 'running' && pct > 0 && pct < 100 ? 'progress-live' : ''}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="tabular text-[12px]">{d.applied_count}/{d.device_count} 台</span>
          </div>
        );
      },
    },
    {
      key: 'deadline', header: '期限',
      cell: (d) => <span className="font-mono text-[11.5px]">{fmtTime(d.deadline_at)}</span>,
    },
    {
      key: 'created_at', header: '建立時間',
      cell: (d) => <span className="font-mono text-[11.5px]">{fmtTime(d.created_at)}</span>,
    },
    {
      key: 'actions', header: '操作',
      cell: (d) => (
        <Button size="sm" variant="secondary" onClick={() => onViewDetail(d.id)}>查看逐台進度</Button>
      ),
    },
  ];

  return (
    <Card className="admin-data-card overflow-hidden">
      <CardHeader>
        <div>
          <CardTitle>部署紀錄</CardTitle>
          <CardDescription>每 5 秒更新。已確認台數包含版本確認，不以下載百分比結案。</CardDescription>
        </div>
      </CardHeader>
      <DataTable<Deployment>
        rows={depsQ.data ?? []}
        columns={columns}
        rowKey={(d) => String(d.id)}
        loading={depsQ.isLoading}
        error={depsQ.error}
        empty={<span className="block space-y-3"><span className="block">尚無部署紀錄。先選擇套件，再確認要更新的裝置。</span><Button variant="secondary" onClick={onChoosePackage}>選擇套件<ArrowRight className="h-4 w-4" /></Button></span>}
      />
    </Card>
  );
}

/* =============================== 部署詳情抽屜 =============================== */

function DepDetailDrawer({ depId, onClose }: { depId: number | null; onClose: () => void }) {
  const queryClient = useQueryClient();

  const depQ = useQuery({
    queryKey: ['deployments', depId],
    queryFn: () => apiGet<DeploymentDetail>(`/api/v1/deployments/${depId}`),
    enabled: depId !== null,
    refetchInterval: depId !== null ? 5_000 : false, // 進度定期更新
  });

  /* 取消部署 */
  const cancelMut = useMutation({
    mutationFn: (id: number) => apiPost(`/api/v1/deployments/${id}/cancel`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deployments'] });
      queryClient.invalidateQueries({ queryKey: ['deployments', depId] });
    },
  });

  /* 回滾部署 */
  const rollbackMut = useMutation({
    mutationFn: (id: number) => apiPost<{ rollback_sent: string[]; rollback_denied: { device_id: string; reason: string }[] }>(`/api/v1/deployments/${id}/rollback`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deployments'] });
      queryClient.invalidateQueries({ queryKey: ['deployments', depId] });
    },
  });

  /* 刪除已結束的部署紀錄：逐台進度一起刪，套件與裝置韌體不受影響 */
  const deleteMut = useMutation({
    mutationFn: (id: number) => apiDelete(`/api/v1/deployments/${id}`),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ['deployments', depId], exact: true });
      queryClient.invalidateQueries({ queryKey: ['deployments'], exact: true });
      queryClient.invalidateQueries({ queryKey: ['packages'] });
      onClose();
    },
  });

  const data = depQ.data;
  const dep = data?.deployment;
  const targets = (data?.targets ?? []) as TargetWithResult[];
  const eligibleTargets = targets.filter((t) => t.state === 'applied' && t.capabilities?.rollback);
  const unsupportedCount = targets.filter((t) => t.state === 'applied' && !t.capabilities?.rollback).length;
  const isTerminal = dep ? ['done', 'partial', 'cancelled', 'expired'].includes(dep.state) : true;
  const activeCount = targets.filter((t) => ['pending', 'sent', 'downloading', 'applying', 'rebooting', 'skipped'].includes(t.state)).length;
  const failedCount = targets.filter((t) => t.state === 'failed' || t.state === 'expired').length;

  return (
    <Drawer
      open={depId !== null}
      onOpenChange={(v) => { if (!v && !deleteMut.isPending) { rollbackMut.reset(); cancelMut.reset(); deleteMut.reset(); onClose(); } }}
      title={dep?.name ?? '部署詳情'}
      wide
    >
      {depQ.isLoading ? (
        <div className="grid place-items-center py-20">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : depQ.error ? (
        <p className="py-6 text-center text-[13px] text-destructive">
          載入失敗：{depQ.error instanceof Error ? depQ.error.message : '未知錯誤'}
        </p>
      ) : dep ? (
        <div className="space-y-4">
          <div>
            <h3 className="text-base font-bold">{dep.name ?? '部署詳情'}</h3>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge variant={DEP_VARIANT[dep.state]}>{DEP_LABEL[dep.state]}</Badge>
              <span className="text-[12px] text-muted-foreground">每 5 秒更新</span>
            </div>
            <p className="mt-1 text-[13px]">
              套件：{dep.pkg_name} · {dep.pkg_version}
            </p>
            <p className="mt-1 text-[12px] text-muted-foreground">完成期限：{fmtTime(dep.deadline_at)}</p>
          </div>
          <div className="grid grid-cols-3 divide-x divide-border rounded-sm border border-border py-3 text-center text-[12px]" role="status" aria-live="polite">
            <div><p className="font-mono text-xl font-bold text-success-on">{dep.applied_count}</p><p className="mt-1">更新已確認</p></div>
            <div><p className="font-mono text-xl font-bold">{activeCount}</p><p className="mt-1">仍在進行</p></div>
            <div><p className="font-mono text-xl font-bold text-destructive">{failedCount}</p><p className="mt-1">失敗／逾期</p></div>
          </div>
          <div className="border-l-2 border-primary bg-secondary/35 px-4 py-3 text-[13px] leading-relaxed">
            <p className="font-semibold">下載 100% 僅代表檔案收齊</p>
            <p className="mt-1 text-muted-foreground">檔案收齊後還要驗證、套用與重開。只有裝置回報完成，且平台確認目標版本，才顯示「更新已確認」。</p>
          </div>

          {/* 操作按鈕 */}
          <div className="flex flex-wrap gap-2">
            {!isTerminal && (
              <Button
                size="sm"
                variant="destructive"
                disabled={cancelMut.isPending}
                onClick={() => {
                  if (!window.confirm('要求取消此部署？未開始的裝置將取消；正在下載的裝置會收到停止要求。已進入切槽或重開階段的裝置可能無法停止，取消追蹤不等於已還原韌體。')) return;
                  cancelMut.mutate(dep.id);
                }}
              >
                {cancelMut.isPending ? '取消中…' : '要求取消部署'}
              </Button>
            )}
            {eligibleTargets.length > 0 && (
              <Button
                size="sm"
                variant="secondary"
                disabled={rollbackMut.isPending || rollbackMut.isSuccess}
                onClick={() => {
                  const names = eligibleTargets.map((t) => t.device_id).join('、');
                  if (!window.confirm(`確定要對此部署中 ${eligibleTargets.length} 台已套用且支援回滾的裝置下發回滾命令嗎？\n裝置：${names}${unsupportedCount ? `\n另有 ${unsupportedCount} 台已套用裝置不支援回滾，將略過。` : ''}`)) return;
                  rollbackMut.mutate(dep.id);
                }}
              >
                {rollbackMut.isPending ? '下發中…' : rollbackMut.isSuccess ? '已下發回滾命令' : `回滾此部署（${eligibleTargets.length} 台）`}
              </Button>
            )}
            {isTerminal && (
              <Button
                size="sm"
                variant="secondary"
                disabled={deleteMut.isPending}
                onClick={() => {
                  if (!window.confirm(`刪除部署「${dep.name ?? dep.id}」的紀錄？\n逐台進度與結果會一併刪除，無法還原；不會影響裝置目前的韌體，也不會刪除套件。`)) return;
                  deleteMut.mutate(dep.id);
                }}
              >
                {deleteMut.isPending ? '刪除中…' : '刪除部署紀錄'}
              </Button>
            )}
          </div>

          {!isTerminal && <p className="text-[12px] leading-relaxed text-muted-foreground">取消只提出停止要求；已切槽或重開中的更新可能無法停止，仍需確認裝置實際版本。</p>}

          {eligibleTargets.length > 0 && (
            <div className="border-l-2 border-primary bg-secondary/40 px-4 py-3 text-[13px]">
              <p className="font-semibold">可回滾：{eligibleTargets.length} 台已套用裝置</p>
              <p className="mt-1 break-words font-mono text-[12px]">{eligibleTargets.map((t) => t.device_id).join('、')}</p>
              {unsupportedCount > 0 && <p className="mt-1 text-muted-foreground">另有 {unsupportedCount} 台已套用裝置不支援回滾，將略過。</p>}
            </div>
          )}
          {eligibleTargets.length === 0 && unsupportedCount > 0 && <p className="text-[13px] text-muted-foreground">已套用裝置均不支援回滾。</p>}
          {rollbackMut.isSuccess && (
            <p role="status" className="text-[13px] text-success-on">
              已向 {rollbackMut.data.rollback_sent.length} 台裝置下發回滾命令：{rollbackMut.data.rollback_sent.join('、')}。
              {rollbackMut.data.rollback_denied.length > 0 && ` 略過：${rollbackMut.data.rollback_denied.map((item) => `${item.device_id}（${item.reason}）`).join('、')}。`}
              請至裝置狀態確認結果。
            </p>
          )}

          {cancelMut.isError && (
            <p className="text-[13px] text-destructive">
              取消失敗：{cancelMut.error instanceof Error ? cancelMut.error.message : '未知錯誤'}
            </p>
          )}
          {rollbackMut.isError && (
            <p className="text-[13px] text-destructive">
              回滾失敗：{rollbackMut.error instanceof Error ? rollbackMut.error.message : '未知錯誤'}
            </p>
          )}
          {deleteMut.isError && (
            <p role="alert" className="break-words text-[13px] text-destructive">
              刪除失敗：{deleteMut.error instanceof Error ? deleteMut.error.message : '未知錯誤'}
            </p>
          )}

          {/* 階段與下載百分比分開，手機也能完整看到失敗原因。 */}
          <div className="space-y-3">
            {targets.map((target) => <TargetProgressCard key={target.id} target={target} version={dep.pkg_version} />)}
            {!targets.length && <p className="py-4 text-[13px] text-muted-foreground">無目標裝置</p>}
          </div>
        </div>
      ) : null}
    </Drawer>
  );
}

function TargetProgressCard({ target, version }: { target: TargetWithResult; version: string }) {
  const pct = Math.max(0, Math.min(100, target.progress ?? 0));
  const confirmed = target.state === 'applied';
  const failed = target.state === 'failed' || target.state === 'expired';
  const result = target.command_result;
  const stages: Partial<Record<TargetState, number>> = { pending: 0, sent: 0, downloading: 1, applying: 2, rebooting: 3, applied: 4 };
  const step = stages[target.state] ?? -1;
  const guidance = target.state === 'rebooting' ? '正在等待新韌體重開、上線與版本確認，請保持供電。'
    : target.state === 'applying' ? '檔案已下載，正在驗證與準備套用；目前尚未確認成功。'
    : target.state === 'downloading' && pct === 100 ? '檔案已收齊，下一步驗證並重開；目前尚未確認成功。'
    : target.state === 'sent' ? '指令已下發，正在等待裝置受理。'
    : target.state === 'skipped' ? '即時觀看中，結束觀看後平台會再嘗試下發。'
    : target.state === 'expired' ? '期限內未取得確認，請檢查裝置版本與連線，再決定是否重新部署。'
    : target.state === 'cancelled' ? '平台已取消追蹤；請確認裝置實際版本，已進入重開階段的更新可能仍會完成。'
    : confirmed ? '裝置已回報完成，平台已確認目標版本。' : '';
  return <section className={`min-w-0 rounded-sm border p-4 ${failed ? 'border-destructive/40 bg-accent/20' : 'border-border bg-card'}`} aria-label={`${target.device_id} 更新進度`}>
    <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
      <p className="min-w-0 break-all font-mono text-[13px] font-semibold">{target.device_id}</p>
      <Badge variant={TGT_VARIANT[target.state]}>{confirmed && <CheckCircle2 className="mr-1 h-3 w-3" />}{TGT_LABEL[target.state]}</Badge>
    </div>
    <ol className="mt-4 grid grid-cols-5 gap-1 text-center text-[11px]" aria-label="更新階段">
      {['下發', '下載', '驗證', '重開', '確認'].map((label, index) => <li key={label} className={`rounded-sm px-1 py-2 ${step >= index ? confirmed ? 'bg-success/10 text-success-on' : 'bg-secondary font-semibold' : 'text-muted-foreground'}`} aria-current={step === index ? 'step' : undefined}>{label}</li>)}
    </ol>
    {!confirmed && !['pending', 'sent', 'skipped', 'cancelled', 'expired'].includes(target.state) && <div className="mt-4">
      <div className="mb-2 flex items-center justify-between gap-2 text-[12px]"><span>裝置回報進度</span><span className="font-mono">{pct}%</span></div>
      <div className="h-1.5 overflow-hidden rounded-full bg-secondary" role="progressbar" aria-label={`${target.device_id} 裝置回報進度（非更新確認）`} aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div className={`h-full transition-all ${failed ? 'bg-destructive' : 'bg-primary'} ${(target.state === 'downloading' || target.state === 'applying') && pct > 0 && pct < 100 ? 'progress-live' : ''}`} style={{ width: `${pct}%` }} />
      </div>
      {typeof result?.downloaded_bytes === 'number' && typeof result.total_bytes === 'number' && <p className="mt-2 font-mono text-[11px] text-muted-foreground">{fmtBytes(result.downloaded_bytes)} / {fmtBytes(result.total_bytes)}</p>}
    </div>}
    <p className="mt-3 break-words text-[13px] leading-relaxed">{guidance}</p>
    {(result?.message || target.detail) && <p className="mt-2 break-words text-[12px] leading-relaxed text-muted-foreground">裝置訊息：{result?.message || target.detail}</p>}
    {result?.reason && <p className="mt-2 break-words text-[13px] leading-relaxed text-destructive">{FAILURE_REASON[result.reason] ?? '裝置回報錯誤'} <span className="break-all font-mono text-[11px]">({result.reason})</span></p>}
    <p className="mt-3 break-all border-t border-border pt-3 text-[12px]">{target.version_before || '原版本未回報'} → {confirmed ? target.version_after || version : `目標 ${version}`}</p>
  </section>;
}
